import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CheckIcon from "@mui/icons-material/Check";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { useColorScheme } from "@mui/material/styles";

import { IconoMorfo } from "./IconoMorfo";
import { cambiarConBarrido } from "../theme/barrido";
import { timing } from "../theme/theme";

/** Los tres ajustes, en el orden en que se entienden: día, noche, delegar. */
const OPCIONES = [
  { valor: "light", trazo: "sol", clave: "theme.light" },
  { valor: "dark", trazo: "luna", clave: "theme.dark" },
  { valor: "system", trazo: "sistema", clave: "theme.system" },
];

/**
 * Elige el tema del portal: claro, oscuro o el que diga el sistema.
 *
 * Son tres opciones y no un interruptor de dos posiciones porque «el que diga
 * el sistema» es una respuesta distinta de las otras dos, no un punto medio:
 * quien lo elige quiere que el portal se ponga oscuro al anochecer con el
 * resto de sus aplicaciones. Con un interruptor esa preferencia solo se puede
 * expresar por accidente, y en cuanto se toca una vez se pierde para siempre.
 *
 * El icono dice cuál de las tres está puesta —sol, luna o monitor— en vez de
 * anunciar a cuál se iría al pulsarlo. Un control que muestra lo contrario de
 * lo que pasa se lee mal la mitad de las veces, y aquí además haría imposible
 * distinguir «oscuro» de «automático, y ahora es de noche».
 *
 * Del cambio en sí se encarga `cambiarConBarrido`: el tema nuevo se derrama
 * desde este botón.
 */
export function BotonTema() {
  const { mode, setMode, systemMode } = useColorScheme();
  const { t } = useTranslation();
  const [ancla, setAncla] = useState(null);
  const botonRef = useRef(null);

  // En el primer pintado todavía no se sabe: MUI lee la preferencia guardada
  // después de montar, para no dar por hecho un tema que quizá no es el suyo.
  const ajuste = mode ?? "system";
  const actual = OPCIONES.find((o) => o.valor === ajuste) ?? OPCIONES[2];
  const resuelto = ajuste === "system" ? (systemMode ?? "light") : ajuste;

  const elegir = (valor) => {
    setAncla(null);
    if (valor === ajuste) return;
    cambiarConBarrido(botonRef.current, () => setMode(valor));
  };

  return (
    <>
      <Tooltip title={t("theme.label")}>
        <IconButton
          ref={botonRef}
          onClick={(evento) => setAncla(evento.currentTarget)}
          aria-label={t("theme.label")}
          aria-haspopup="menu"
          sx={{
            // El sol de día y la luna de noche giran en sentidos contrarios
            // al asentarse, de modo que el icono no solo cambia de forma:
            // acompaña al barrido en la dirección en la que va el cambio.
            transition: `color ${timing.menu}ms ease`,
            color: resuelto === "dark" ? "warning.main" : "inherit",
          }}
        >
          <IconoMorfo trazo={actual.trazo} />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={ancla}
        open={Boolean(ancla)}
        onClose={() => setAncla(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 196 } } }}
      >
        {OPCIONES.map((opcion) => (
          <MenuItem
            key={opcion.valor}
            selected={opcion.valor === ajuste}
            onClick={() => elegir(opcion.valor)}
          >
            <ListItemIcon sx={{ minWidth: 34 }}>
              <IconoMorfo trazo={opcion.trazo} size={18} />
            </ListItemIcon>
            <ListItemText slotProps={{ primary: { variant: "body2" } }}>
              {t(opcion.clave)}
            </ListItemText>
            {opcion.valor === ajuste && (
              <CheckIcon sx={{ fontSize: 16, ml: 1, color: "primary.main" }} />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
